// SiteViewport2D.tsx — 2D waterfall layout showing ALL checked elevations
// Each elevation renders on its own row with panels L→R
// Click an elevation row to switch focus (highlight)
// Drag panels within a row to reorder them

import { useState, useRef, useCallback, useEffect } from 'react';
import type { PanelData, Elevation, CornerType } from '../types/panelvar';
import { MAX_ELEVATIONS, CORNER_TYPE_LABELS } from '../types/panelvar';
import { detectPanelClashes } from '../utils/clashDetection';

const PRIMARY = new Set(['B', 'F', 'R', 'L']);
const CORNER_OPTIONS: CornerType[] = ['continuous', 'butt-int', 'butt-ext', 'miter', 'pilaster', 'l-panel', 'gap'];

interface Props {
  elevations: Elevation[];          // checked elevations to display
  allElevations: Elevation[];       // all elevations (for split limit check + connection targets)
  highlightId: string | null;       // single-clicked (highlighted) elevation
  panels: PanelData[];
  selectedPanelIds: string[];
  onPanelClick: (panelId: string, ctrlKey: boolean) => void;
  onPanelDoubleClick: (panelId: string) => void;
  onPanelContextMenu: (panelId: string, screenX: number, screenY: number) => void;
  onElevationClick: (elevId: string) => void;
  onReorderPanel: (elevationId: string, fromIndex: number, toIndex: number) => void;
  onMovePanelBetween: (fromElevId: string, panelIdx: number, toElevId: string, toIdx: number) => void;
  onSort: (elevId: string, order: 'asc' | 'desc') => void;
  onSplit: (elevId: string, sections: number, panelsPerSection: number[]) => void;
  onSetCorner: (elevId: string, side: 'left' | 'right', type: CornerType) => void;
  onSetConnection: (elevId: string, side: 'left' | 'right', targetId: string | undefined) => void;
  onRubberbandSelect: (ids: string[]) => void;
}

const SCALE = 0.06;         // inches → px scale factor
const GAP = 12;             // px gap between panels
const MIN_W = 40;           // minimum rendered width px
const MIN_H = 60;           // minimum rendered height px

function ElevationRow({ elevation, panels, allElevations, allElevationCount, isHighlighted, selectedPanelIds, onPanelClick, onPanelDoubleClick, onPanelContextMenu, onElevationClick, onReorderPanel, onMovePanelBetween, onSort, onSplit, onSetCorner, onSetConnection }: {
  elevation: Elevation;
  panels: PanelData[];
  allElevations: Elevation[];
  allElevationCount: number;
  isHighlighted: boolean;
  selectedPanelIds: Set<string>;
  onPanelClick: (panelId: string, ctrlKey: boolean) => void;
  onPanelDoubleClick: (panelId: string) => void;
  onPanelContextMenu: (panelId: string, screenX: number, screenY: number) => void;
  onElevationClick: () => void;
  onReorderPanel: (fromIndex: number, toIndex: number) => void;
  onMovePanelBetween: (fromElevId: string, panelIdx: number, toIdx: number) => void;
  onSort: (order: 'asc' | 'desc') => void;
  onSplit: (sections: number, panelsPerSection: number[]) => void;
  onSetCorner: (side: 'left' | 'right', type: CornerType) => void;
  onSetConnection: (side: 'left' | 'right', targetId: string | undefined) => void;
}) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [lastSortDir, setLastSortDir] = useState<'asc' | 'desc' | null>(null);
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitSections, setSplitSections] = useState(2);
  const [splitCounts, setSplitCounts] = useState<number[]>([]);

  const placed = elevation.panelIds
    .map(pid => panels.find(p => p.id === pid))
    .filter((p): p is PanelData => p != null);

  // ── Split helpers ────────────────────────────────
  const panelCount = elevation.panelIds.length;
  const splitTotal = splitCounts.reduce((s, n) => s + n, 0);
  const cloneCount = Math.max(0, splitTotal - panelCount);
  const wouldExceed = allElevationCount - 1 + splitSections > MAX_ELEVATIONS;

  function defaultDistribution(total: number, secs: number): number[] {
    const base = Math.floor(total / secs);
    const remainder = total - base * secs;
    const center = Math.floor(secs / 2);
    return Array.from({ length: secs }, (_, i) => base + (i === center ? remainder : 0));
  }

  function openSplit() {
    setSplitOpen(true);
    setSplitSections(2);
    setSplitCounts(defaultDistribution(panelCount, 2));
  }

  function handleSplitSectionsChange(secs: number) {
    setSplitSections(secs);
    setSplitCounts(defaultDistribution(panelCount, secs));
  }

  function handleSplitCountChange(idx: number, val: number) {
    const next = [...splitCounts];
    next[idx] = Math.max(0, val);
    setSplitCounts(next);
  }

  function commitSplit() {
    onSplit(splitSections, splitCounts);
    setSplitOpen(false);
  }

  // ── Label bar (shared between empty and non-empty) ──
  const labelBar = (
    <div className="site-viewport-label">
      <span>{elevation.direction} — {elevation.name} ({placed.length} panel{placed.length !== 1 ? 's' : ''})</span>
      <span className="site-viewport-label-actions">
        {placed.length > 1 && (
          <button
            className="site-sidebar-sort-btn"
            onClick={ev => {
              ev.stopPropagation();
              const next = lastSortDir === 'asc' ? 'desc' : 'asc';
              setLastSortDir(next);
              onSort(next);
            }}
            title={`Sort panels ${lastSortDir === 'asc' ? 'Z→A' : 'A→Z'} (click to toggle)`}
          >{lastSortDir === 'asc' ? '↓' : '↑'}</button>
        )}
        {PRIMARY.has(elevation.direction) && panelCount > 0 && (
          <button
            className="site-sidebar-split-btn"
            onClick={ev => { ev.stopPropagation(); openSplit(); }}
            title="Split elevation into sections"
          >✂</button>
        )}
      </span>
    </div>
  );

  // ── Inline split form ────────────────────────────
  const splitForm = splitOpen && (
    <div className="site-sidebar-split-form" onClick={ev => ev.stopPropagation()}>
      <div className="site-sidebar-split-row">
        <label>Sections:</label>
        <input
          type="number"
          min={2}
          max={5}
          value={splitSections}
          onChange={ev => handleSplitSectionsChange(Math.min(5, Math.max(2, parseInt(ev.target.value) || 2)))}
          className="site-sidebar-split-num"
        />
      </div>
      <div className="site-sidebar-split-inputs">
        {splitCounts.map((c, i) => (
          <div key={i} className="site-sidebar-split-field">
            <label>§{i + 1}</label>
            <input
              type="number"
              min={0}
              value={c}
              onChange={ev => handleSplitCountChange(i, parseInt(ev.target.value) || 0)}
              className="site-sidebar-split-num"
            />
          </div>
        ))}
      </div>
      <div className="site-sidebar-split-summary">
        {splitTotal} of {panelCount} panel{panelCount !== 1 ? 's' : ''}
        {cloneCount > 0 && (
          <span className="site-sidebar-split-warn"> ({cloneCount} will be cloned)</span>
        )}
      </div>
      {wouldExceed && (
        <div className="site-sidebar-split-error">Would exceed {MAX_ELEVATIONS} elevations</div>
      )}
      <div className="site-sidebar-split-actions">
        <button
          className="site-sidebar-ok-btn"
          onClick={commitSplit}
          disabled={wouldExceed}
        >Split</button>
        <button
          className="site-sidebar-cancel-btn"
          onClick={() => setSplitOpen(false)}
        >Cancel</button>
      </div>
    </div>
  );

  if (placed.length === 0) {
    return (
      <div
        className={`site-viewport-elevation-row${isHighlighted ? ' highlighted' : ''}`}
        onClick={onElevationClick}
        onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) { setDragOverIdx(null); setDragIdx(null); } }}
        onDrop={e => {
          const raw = e.dataTransfer.getData('application/cv-panel');
          if (raw) {
            try {
              const src = JSON.parse(raw) as { elevId: string; idx: number };
              if (src.elevId !== elevation.id) {
                onMovePanelBetween(src.elevId, src.idx, 0);
              }
            } catch { /* ignore */ }
          }
        }}
      >
        {labelBar}
        <div className="site-viewport-empty-row">No panels assigned — drop here</div>
      </div>
    );
  }

  const rects = placed.map(p => ({
    id: p.id,
    mark: p.mpvar.panelMark,
    w: Math.max(p.mpvar.width * SCALE, MIN_W),
    h: Math.max(p.mpvar.height * SCALE, MIN_H),
    widthIn: p.mpvar.width,
    heightIn: p.mpvar.height,
  }));

  // Panel IDs that have per-panel clashes (feature-vs-feature or feature-vs-edge)
  const clashIds = new Set(placed.filter(p => detectPanelClashes(p).length > 0).map(p => p.id));

  const maxH = Math.max(...rects.map(r => r.h));

  function handleDragStart(e: React.DragEvent, idx: number) {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/cv-panel', JSON.stringify({ elevId: elevation.id, idx }));
    setDragIdx(idx);
  }
  function handleDragOver(e: React.DragEvent, idx: number) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIdx(idx);
  }
  function handleDrop(e: React.DragEvent, idx: number) {
    const raw = e.dataTransfer.getData('application/cv-panel');
    if (raw) {
      try {
        const src = JSON.parse(raw) as { elevId: string; idx: number };
        if (src.elevId !== elevation.id) {
          onMovePanelBetween(src.elevId, src.idx, idx);
          setDragIdx(null);
          setDragOverIdx(null);
          return;
        }
      } catch { /* fall through to local reorder */ }
    }
    if (dragIdx !== null && dragIdx !== idx) {
      onReorderPanel(dragIdx, idx);
    }
    setDragIdx(null);
    setDragOverIdx(null);
  }
  function handleDragEnd() {
    setDragIdx(null);
    setDragOverIdx(null);
  }

  return (
    <div
      className={`site-viewport-elevation-row${isHighlighted ? ' highlighted' : ''}`}
      onClick={onElevationClick}
      onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) { setDragOverIdx(null); setDragIdx(null); } }}
      onDrop={e => {
        const raw = e.dataTransfer.getData('application/cv-panel');
        if (raw) {
          try {
            const src = JSON.parse(raw) as { elevId: string; idx: number };
            if (src.elevId !== elevation.id) {
              onMovePanelBetween(src.elevId, src.idx, placed.length);
            }
          } catch { /* ignore */ }
        }
        setDragIdx(null);
        setDragOverIdx(null);
      }}
    >
      {labelBar}
      {splitForm}
      <div className="site-panel-row" style={{ gap: GAP }}>
        <div className="site-corner-group">
          <select
            className="site-corner-selector"
            value={elevation.leftCorner ?? 'continuous'}
            onChange={e => { e.stopPropagation(); onSetCorner('left', e.target.value as CornerType); }}
            onClick={e => e.stopPropagation()}
            title="Left corner condition"
          >
            {CORNER_OPTIONS.map(ct => <option key={ct} value={ct}>{CORNER_TYPE_LABELS[ct]}</option>)}
          </select>
          <select
            className="site-corner-selector site-corner-connection"
            value={elevation.leftConnectsTo ?? ''}
            onChange={e => { e.stopPropagation(); onSetConnection('left', e.target.value || undefined); }}
            onClick={e => e.stopPropagation()}
            title="Left connects to"
          >
            <option value="">—</option>
            {allElevations.filter(ae => ae.id !== elevation.id).map(ae => (
              <option key={ae.id} value={ae.id}>{ae.direction} — {ae.name}</option>
            ))}
          </select>
        </div>
        {rects.map((r, idx) => (
          <div
            key={r.id}
            className={
              'site-panel-card'
              + (selectedPanelIds.has(r.id) ? ' selected' : '')
              + (clashIds.has(r.id) ? ' clash' : '')
              + (dragIdx === idx ? ' dragging' : '')
              + (dragOverIdx === idx && dragIdx !== idx ? ' drag-over' : '')
            }
            style={{ width: r.w, height: r.h, marginTop: maxH - r.h }}
            draggable
            onDragStart={e => handleDragStart(e, idx)}
            onDragOver={e => handleDragOver(e, idx)}
            onDrop={e => handleDrop(e, idx)}
            onDragEnd={handleDragEnd}
            onClick={e => { e.stopPropagation(); onPanelClick(r.id, e.ctrlKey || e.metaKey); }}
            onDoubleClick={e => { e.stopPropagation(); onPanelDoubleClick(r.id); }}
            onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onPanelContextMenu(r.id, e.clientX, e.clientY); }}
          >
            <span className="site-panel-card-mark">{r.mark}</span>
            <span className="site-panel-card-dim">{Math.round(r.widthIn / 12)}' × {Math.round(r.heightIn / 12)}'</span>
            {clashIds.has(r.id) && <span className="site-panel-card-clash" title="Spatial clash detected">⚠</span>}
          </div>
        ))}
        <div className="site-corner-group">
          <select
            className="site-corner-selector"
            value={elevation.rightCorner ?? 'continuous'}
            onChange={e => { e.stopPropagation(); onSetCorner('right', e.target.value as CornerType); }}
            onClick={e => e.stopPropagation()}
            title="Right corner condition"
          >
            {CORNER_OPTIONS.map(ct => <option key={ct} value={ct}>{CORNER_TYPE_LABELS[ct]}</option>)}
          </select>
          <select
            className="site-corner-selector site-corner-connection"
            value={elevation.rightConnectsTo ?? ''}
            onChange={e => { e.stopPropagation(); onSetConnection('right', e.target.value || undefined); }}
            onClick={e => e.stopPropagation()}
            title="Right connects to"
          >
            <option value="">—</option>
            {allElevations.filter(ae => ae.id !== elevation.id).map(ae => (
              <option key={ae.id} value={ae.id}>{ae.direction} — {ae.name}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

export default function SiteViewport2D({ elevations, allElevations, highlightId, panels, selectedPanelIds, onPanelClick, onPanelDoubleClick, onPanelContextMenu, onElevationClick, onReorderPanel, onMovePanelBetween, onSort, onSplit, onSetCorner, onSetConnection, onRubberbandSelect }: Props) {
  const selSet = useState(() => new Set(selectedPanelIds))[0];
  // Keep selSet in sync — reconstruct on every render (cheap for small sets)
  selSet.clear();
  for (const id of selectedPanelIds) selSet.add(id);

  // ── Rubberband state ───────────────────────────────────
  const containerRef = useRef<HTMLDivElement>(null);
  const [rbStart, setRbStart] = useState<{ x: number; y: number } | null>(null);
  const [rbEnd, setRbEnd] = useState<{ x: number; y: number } | null>(null);
  const rbActive = rbStart && rbEnd && (Math.abs(rbEnd.x - rbStart.x) > 4 || Math.abs(rbEnd.y - rbStart.y) > 4);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Only start rubberband on left click on empty area
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest('.site-panel-card') || target.closest('.site-corner-group') || target.closest('select') || target.closest('button')) return;
    const rect = containerRef.current!.getBoundingClientRect();
    setRbStart({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setRbEnd({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!rbStart) return;
    const rect = containerRef.current!.getBoundingClientRect();
    setRbEnd({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, [rbStart]);

  const handleMouseUp = useCallback(() => {
    if (rbStart && rbEnd && rbActive) {
      // Find all panel cards inside the rubberband rect
      const container = containerRef.current;
      if (container) {
        const cRect = container.getBoundingClientRect();
        const x1 = Math.min(rbStart.x, rbEnd.x);
        const y1 = Math.min(rbStart.y, rbEnd.y);
        const x2 = Math.max(rbStart.x, rbEnd.x);
        const y2 = Math.max(rbStart.y, rbEnd.y);
        const cards = container.querySelectorAll<HTMLElement>('.site-panel-card');
        const ids: string[] = [];
        cards.forEach(card => {
          const cr = card.getBoundingClientRect();
          const cx = cr.left - cRect.left + cr.width / 2;
          const cy = cr.top - cRect.top + cr.height / 2;
          if (cx >= x1 && cx <= x2 && cy >= y1 && cy <= y2) {
            // Extract panel ID from the elevation data
            const mark = card.querySelector('.site-panel-card-mark')?.textContent;
            if (mark) {
              const panel = panels.find(p => p.mpvar.panelMark === mark);
              if (panel) ids.push(panel.id);
            }
          }
        });
        if (ids.length > 0) onRubberbandSelect(ids);
      }
    }
    setRbStart(null);
    setRbEnd(null);
  }, [rbStart, rbEnd, rbActive, panels, onRubberbandSelect]);

  // Cancel rubberband on escape
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') { setRbStart(null); setRbEnd(null); }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (elevations.length === 0) {
    return (
      <div className="site-viewport-empty">
        <p>No elevations checked — check elevations in the sidebar</p>
      </div>
    );
  }

  const rbRect = rbActive ? {
    left: Math.min(rbStart!.x, rbEnd!.x),
    top: Math.min(rbStart!.y, rbEnd!.y),
    width: Math.abs(rbEnd!.x - rbStart!.x),
    height: Math.abs(rbEnd!.y - rbStart!.y),
  } : null;

  return (
    <div
      ref={containerRef}
      className="site-viewport-2d"
      style={{ overflowY: 'auto', position: 'relative' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {elevations.map(e => (
        <ElevationRow
          key={e.id}
          elevation={e}
          panels={panels}
          allElevations={allElevations}
          allElevationCount={allElevations.length}
          isHighlighted={e.id === highlightId}
          selectedPanelIds={selSet}
          onPanelClick={onPanelClick}
          onPanelDoubleClick={onPanelDoubleClick}
          onPanelContextMenu={onPanelContextMenu}
          onElevationClick={() => onElevationClick(e.id)}
          onReorderPanel={(from, to) => onReorderPanel(e.id, from, to)}
          onMovePanelBetween={(fromElevId, fromIdx, toIdx) => onMovePanelBetween(fromElevId, fromIdx, e.id, toIdx)}
          onSort={(order) => onSort(e.id, order)}
          onSplit={(secs, counts) => onSplit(e.id, secs, counts)}
          onSetCorner={(side, type) => onSetCorner(e.id, side, type)}
          onSetConnection={(side, targetId) => onSetConnection(e.id, side, targetId)}
        />
      ))}
      {rbRect && (
        <div className="site-rubberband" style={rbRect} />
      )}
    </div>
  );
}
